import React, { useEffect, useState, useCallback } from 'react'
import { withRouter } from 'react-router-dom'
import { DivStyled, ContainerStyled } from './styled'
import { Button, message, Popconfirm } from 'antd'
import get from 'lodash/get';
import ReactTable from 'react-table'
import moment from 'moment'
import 'react-table/react-table.css'
import Loading from '$app/shared/components/Loading'
import { useMaiaT } from '$app/i18n/useMaiaT';
import Menu from '../components/Menu'
import GaragePageHeader from '../components/GaragePageHeader';
import CreateEditModal from './components/CreateEditModal/index'
import { getGarageById } from '../GarageEditPage/actions';

import {
  listGarageUsers,
  createGarageAccount,
  editUser,
  deleteUser,
  requestPasswordReset,
} from './actions'

const USER_LIST_RETRY_LIMIT = 5;
const USER_LIST_RETRY_INITIAL_DELAY_MS = 300;

export const GarageUsersPage = ({ history, match }) => {
  const garageId = match.params.garageId;
  const t = useMaiaT();
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [users, setUsers] = useState([])
  const [visible, setVisible] = useState(false)
  const [editing, setEditing] = useState(null)
  const [garage, setGarage] = useState(null)
  const [resettingUserId, setResettingUserId] = useState(null)

  const getUsers = useCallback(async () => {
    const users = await listGarageUsers(garageId);
    const list = Array.isArray(users) ? users : [];
    setUsers(list);
    return list;
  }, [garageId]);

  const refreshUsersUntil = useCallback(async (isReady) => {
    setLoadingUsers(true);
    try {
      for (let attempt = 0; attempt < USER_LIST_RETRY_LIMIT; attempt += 1) {
        // Immediate GETs after a mutation are often stale; wait once so the first
        // request can succeed. Later retries already wait a full round trip.
        if (attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, USER_LIST_RETRY_INITIAL_DELAY_MS));
        }
        const list = await getUsers();
        if (isReady(list)) {
          return;
        }
      }
    } catch (error) {
      console.error(error);
      message.error(get(error, 'response.data.message', 'An error occurred'));
    } finally {
      setLoadingUsers(false);
    }
  }, [getUsers]);

  useEffect(() => {
    let cancelled = false;

    setLoadingUsers(true);
    listGarageUsers(garageId)
      .then((users) => {
        if (cancelled) {
          return;
        }
        setUsers(Array.isArray(users) ? users : []);
        setLoadingUsers(false);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        console.error(error);
        message.error(get(error, 'response.data.message', 'An error occurred'));
      });

    return () => {
      cancelled = true;
    }
  }, [garageId]);

  useEffect(() => {
    let cancelled = false;
    setGarage(null);

    if (!garageId) return;

    getGarageById(garageId)
      .then((_garage) => {
        if (!cancelled) {
          setGarage(_garage);
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        console.error(error);
        message.error(get(error, 'response.data.message', 'An error occurred'));
      });

    return () => {
      cancelled = true;
    };
  }, [garageId]);

  const createAccount = useCallback(async data => {
    const { email, name, phoneNumber } = data
    const template = {
      garageId,
      email,
      name,
      phoneNumber,
      segment: garage.segment,
    }
    try {
      const created = await createGarageAccount(garageId, template);
      setVisible(false);
      if (created && created.linkedExistingLogin) {
        message.success(t(
          'maia.userAccounts.section.linkedExistingLogin',
          'This email already has a login. It can now open this garage too.',
        ));
      }
      await refreshUsersUntil((list) =>
        list.some((user) => (user.email || '').toLowerCase() === email.toLowerCase())
      );
    } catch (err) {
      console.error(err);
      message.error(get(err, 'response.data.message', 'An error occurred'));
    }
  }, [garage, garageId, refreshUsersUntil, t])

  const editAccount = useCallback(async data => {
    try {
      await editUser(garageId, data);
      setVisible(false);
      setEditing(null);
      await refreshUsersUntil((list) => {
        const user = list.find((item) => item.userId === data.userId);
        return Boolean(
          user
          && user.name === data.name
          && get(user, 'userMetadata.phoneNumber') === data.phoneNumber
        );
      });
    } catch (err) {
      console.error(err);
      message.error(get(err, 'response.data.message', 'An error occurred'));
    }
  }, [garageId, refreshUsersUntil])

  const deleteAccount = useCallback(async data => {
    try {
      await deleteUser(garageId, data);
      setVisible(false);
      setEditing(null);
      await refreshUsersUntil((list) => list.every((user) => user.userId !== data));
    } catch (err) {
      console.error(err);
      message.error(get(err, 'response.data.message', 'An error occurred'));
    }
  }, [garageId, refreshUsersUntil])

  const sendPasswordReset = useCallback(async (user) => {
    setResettingUserId(user.userId);
    try {
      await requestPasswordReset(garageId, user.userId);
      message.success(t(
        'maia.userAccounts.section.passwordResetSent',
        'Password reset email sent',
      ));
    } catch (err) {
      console.error(err);
      message.error(get(err, 'response.data.message', 'An error occurred'));
    } finally {
      setResettingUserId(null);
    }
  }, [garageId, t]);

  const columns = [
    {
      Header: t('maia.userAccounts.section.tableColumnLabels.fullName', 'Full name'),
      accessor: 'name'
    },
    {
      Header: t('maia.userAccounts.section.tableColumnLabels.phone', 'Phone'),
      accessor: 'userMetadata.phoneNumber'
    },
    {
      Header: t('maia.userAccounts.section.tableColumnLabels.email', 'Email'),
      accessor: 'email'
    },
    {
      Header: t('maia.userAccounts.section.tableColumnLabels.signedUpDate', 'Signed up date'),
      accessor: 'createdAt',
      Cell: ({ original }) =>  moment(original.createdAt).format('Do MMM YYYY, h:mm A')
    },
    {
      Header: '',
      Cell: ({ original }) => {
        const resetting = resettingUserId === original.userId;
        return (
          <>
            <Popconfirm
              title={t(
                'maia.userAccounts.section.passwordResetConfirm',
                'Send a password reset email to {email}?',
                { email: original.email },
              )}
              onConfirm={() => sendPasswordReset(original)}
              okText={t('maia.userAccounts.section.passwordResetCta', 'Send password reset')}
              cancelText={t('maia.userAccounts.createEditModal.cancelCta', 'Cancel')}
            >
              <Button type="link" loading={resetting} disabled={resetting}>
                {t('maia.userAccounts.section.passwordResetCta', 'Send password reset')}
              </Button>
            </Popconfirm>
            <Button type="link"
              onClick={() => {
                setEditing(original)
                setVisible(true)
              }}
            >{t('maia.userAccounts.section.tableColumnLabels.editCta', 'Edit')}</Button>
          </>
        )
      }
    }
  ]

  if (garageId && garage) {
    return (
      <ContainerStyled>
        <GaragePageHeader history={history} name={garage?.businessName} />
        <Menu selectedItem="users" garageId={garageId} history={history} />

        <DivStyled>
          {
            loadingUsers ? (
              <Loading />
            ) : 
            <>
              {
                users.length !== 0 ?
                  (
                    <ReactTable
                      data={users}
                      columns={columns}
                      showPagination={false}
                      pageSize={users.length}
                    />
                  ) : (
                    <div style={{ margin: 10 }}>{t('maia.userAccounts.section.noAccounts', 'No account has been created for this garage yet.')}</div>
                  )
              }
              <Button
                type='primary'
                onClick={() => setVisible(true)}
                style={{ marginTop: 10 }}
              >+ {t('maia.userAccounts.section.createAccountCta', 'Create account')}</Button>
            </>
          }

        </DivStyled>

        <CreateEditModal
          visible={visible}
          segment={garage?.segment}
          onCancel={() => {
            setVisible(false)
            setEditing(null)
          }}
          onCreate={createAccount}
          onEdit={editAccount}
          editing={editing}
          onDelete={deleteAccount}
        />
      </ContainerStyled>
    );
  }

  return <Loading />;
}

export default withRouter(GarageUsersPage)
